var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Stack, TextField, Typography, } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useCreatePitch, useRetrieveVenue } from "../api/VenueApiProvider";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import ErrorSnackbar from "../utils/ErrorSnackbar";
var SIZE_OPTIONS = [
    { value: "5", label: "5-a-side" },
    { value: "6", label: "6-a-side" },
    { value: "7", label: "7-a-side" },
    { value: "8", label: "8-a-side" },
    { value: "9", label: "9-a-side" },
    { value: "11", label: "11-a-side" },
];
var SURFACE_OPTIONS = [
    { value: "grass", label: "Grass" },
    { value: "artificial_turf", label: "Artificial Turf" },
    { value: "astroturf", label: "Astroturf" },
    { value: "indoor", label: "Indoor" },
    { value: "hybrid", label: "Hybrid" },
];
var CreatePitch = function () {
    var navigate = useNavigate();
    var venueId = useParams().venueId;
    var venueIdNum = venueId ? parseInt(venueId, 10) : undefined;
    var _a = useForm({
        defaultValues: {
            name: "",
            description: "",
            sizes: [],
            surface: "artificial_turf",
            isIndoor: false,
        },
    }), control = _a.control, handleSubmit = _a.handleSubmit, errors = _a.formState.errors;
    var _b = useRetrieveVenue({
        id: venueIdNum,
        enabled: !!venueIdNum,
    }), venue = _b.data, isLoadingVenue = _b.isLoading, venueError = _b.error;
    var createMutation = useCreatePitch({
        onSuccess: function () {
            navigate("/venues/".concat(venueIdNum));
        },
    });
    var onSubmit = function (data) {
        if (!venueIdNum)
            return;
        createMutation.mutate(__assign({ venue: venueIdNum }, data));
    };
    var handleCancel = function () {
        navigate("/venues/".concat(venueIdNum));
    };
    if (isLoadingVenue) {
        return _jsx(Loading, {});
    }
    if (venueError || !venue) {
        return _jsx(ErrorView, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(ErrorSnackbar, { open: createMutation.isError, message: "Failed to add pitch. Please try again.", onClose: function () { return createMutation.reset(); } }), _jsx(Typography, { variant: "h4", gutterBottom: true, sx: { fontSize: { xs: "1.75rem", sm: "2.125rem" } }, children: "Add Pitch" }), _jsxs(Typography, { variant: "body1", color: "text.secondary", sx: { mb: 3 }, children: ["Adding pitch to ", venue.name] }), _jsx(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, children: _jsxs(Stack, { spacing: 3, children: [_jsx(Controller, { name: "name", control: control, rules: { required: "Pitch name is required" }, render: function (_a) {
                                var _b;
                                var field = _a.field;
                                return (_jsx(TextField, __assign({}, field, { label: "Pitch Name", placeholder: "e.g., Pitch 1, Main Field", fullWidth: true, required: true, error: !!errors.name, helperText: (_b = errors.name) === null || _b === void 0 ? void 0 : _b.message })));
                            } }), _jsx(Controller, { name: "description", control: control, render: function (_a) {
                                var field = _a.field;
                                return (_jsx(TextField, __assign({}, field, { label: "Description", placeholder: "e.g., Front left corner, near parking", fullWidth: true, helperText: "Optional description to help identify this pitch" })));
                            } }), _jsx(Controller, { name: "sizes", control: control, rules: {
                                validate: function (value) {
                                    return value.length > 0 || "At least one size is required";
                                },
                            }, render: function (_a) {
                                var field = _a.field;
                                return (_jsxs(FormControl, { fullWidth: true, required: true, error: !!errors.sizes, children: [_jsx(InputLabel, { id: "sizes-label", children: "Supported Sizes" }), _jsx(Select, __assign({}, field, { labelId: "sizes-label", label: "Supported Sizes", multiple: true, input: _jsx(OutlinedInput, { label: "Supported Sizes" }), renderValue: function (selected) { return (_jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 0.5 }, children: selected.map(function (value) { return (_jsx(Chip, { label: "".concat(value, "-a-side"), size: "small" }, value)); }) })); }, children: SIZE_OPTIONS.map(function (option) { return (_jsx(MenuItem, { value: option.value, children: option.label }, option.value)); }) })), errors.sizes && (_jsx(FormHelperText, { children: errors.sizes.message }))] }));
                            } }), _jsx(Controller, { name: "surface", control: control, rules: { required: "Surface is required" }, render: function (_a) {
                                var field = _a.field;
                                return (_jsxs(FormControl, { fullWidth: true, required: true, error: !!errors.surface, children: [_jsx(InputLabel, { id: "surface-label", children: "Surface" }), _jsx(Select, __assign({}, field, { labelId: "surface-label", label: "Surface", children: SURFACE_OPTIONS.map(function (option) { return (_jsx(MenuItem, { value: option.value, children: option.label }, option.value)); }) }))] }));
                            } }), _jsx(Controller, { name: "isIndoor", control: control, render: function (_a) {
                                var field = _a.field;
                                return (_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: field.value, onChange: function (e) { return field.onChange(e.target.checked); } }), label: "Indoor pitch" }));
                            } }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { mt: 1 }, children: [_jsx(Button, { type: "submit", variant: "contained", color: "primary", fullWidth: true, disabled: createMutation.isLoading, children: createMutation.isLoading ? "Adding..." : "Add Pitch" }), _jsx(Button, { onClick: handleCancel, variant: "outlined", color: "secondary", fullWidth: true, children: "Cancel" })] })] }) })] }));
};
export default CreatePitch;
